import { useContext, useEffect, useRef } from 'react';
import { AppContext } from 'context/app-context';
import { addsToVault, vaultAccess } from 'helpers/sharing';
import { VaultNavigationTabs } from 'common/enums';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { DashboardScope } from 'context/vault-detail-context/context-reducer';
import { classnames } from 'helpers/utils';
import { useHideOnScroll } from 'hooks/use-hide-on-scroll';
import { navigationTabs } from 'pages/vault-detail/components/tabs/navigation-tabs';
import { SortDropdown } from 'pages/vault-detail/components/sort-dropdown';
import styles from './vault-tabs-bar.module.scss';

// The owner and those who add to the vault (see useScopedAlbums for what each
// tab lists).
const scopeTabs: { key: DashboardScope; label: string }[] = [
  { key: 'all', label: 'My Stuff' },
  { key: 'shared', label: 'Shared With Me' },
];

// Someone else's vault, for those who only see it (viewers, album or memory
// shares): all of it was shared with them, and nothing in it is theirs.
const viewOnlyTabs: { key: DashboardScope; label: string }[] = [
  { key: 'shared', label: 'Shared With Me' },
];

interface VaultTabsBarProps {
  selectedTab: VaultNavigationTabs;
  onSelectTab: (tab: VaultNavigationTabs) => void;
}

export const VaultTabsBar = ({
  selectedTab,
  onSelectTab,
}: VaultTabsBarProps) => {
  const { state, dispatch } = useContext(VaultDetailContext);
  const { state: appState } = useContext(AppContext);
  const { scope } = state;
  const { currentVault } = appState;
  // Owners, and editors and contributors (of the vault or of an album in it),
  // have their own stuff here (see the dashboard); everyone else only what
  // was shared with them.
  const tabs =
    !currentVault || addsToVault(vaultAccess(appState.data.user, currentVault))
      ? scopeTabs
      : viewOnlyTabs;
  const barRef = useRef<HTMLDivElement>(null);
  // Slides away under the top bar on scrolling down; back on scrolling up or
  // changing tab.
  const hidden = useHideOnScroll(barRef, selectedTab);

  // A scope left over from another vault falls back to this one's tab.
  useEffect(() => {
    if (!tabs.some((tab) => tab.key === scope)) {
      dispatch({
        type: vaultDetailActions.SET_DASHBOARD_SCOPE,
        scope: tabs[0].key,
      });
    }
  }, [tabs, scope]);

  return (
    <div
      ref={barRef}
      className={classnames(styles.tabsBar, { [styles.tabsBarHidden]: hidden })}
    >
      <div className={styles.scopeZone}>
        <div className={styles.scopeTabs}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={classnames(styles.scopeTab, {
                [styles.scopeTabActive]: scope === tab.key,
              })}
              onClick={() =>
                dispatch({
                  type: vaultDetailActions.SET_DASHBOARD_SCOPE,
                  scope: tab.key,
                })
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.pill}>
        {navigationTabs.map((tab) => (
          <button
            key={tab.name}
            type="button"
            className={classnames(styles.pillTab, {
              [styles.pillTabActive]: selectedTab === tab.tab,
            })}
            onClick={() => onSelectTab(tab.tab)}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {/* The dashboard sorts from its Memories header instead. */}
      <div className={styles.sortZone}>
        {selectedTab !== VaultNavigationTabs.dashboard && <SortDropdown />}
      </div>
    </div>
  );
};
